"use client";

import { createContext, useState, useEffect, useMemo, useContext } from 'react';
import { usePathname } from 'next/navigation';
import APP_CONFIG from '@/config/app.config';
import { getMergedTaxConfig } from '@/services/taxSettingsService';
import { getHomepageBanner1OverrideFromSettings, getHomepageBanner2OverrideFromSettings } from '@/services/settingsService';
import { logger } from '@/utils/logger';

const defaultSiteConfig = {
    siteName: APP_CONFIG.site.name,
    domain: APP_CONFIG.site.domain,
    contact: APP_CONFIG.site.contact,
    social: APP_CONFIG.site.social,
    hours: APP_CONFIG.site.hours,
    copyrightText: `(c) ${new Date().getFullYear()} ${APP_CONFIG.site.name}. All rights reserved. Made with love in India`,
    homepageBannerMode: 'default',
    homepageBanner1: null,
    homepageBanner2: null,
    tax: APP_CONFIG.site?.tax || {},
    features: APP_CONFIG.features,
    loading: false,
};

const SiteConfigContext = createContext(defaultSiteConfig);

const DYNAMIC_SETTINGS_ROUTES = ['/checkout', '/payment', '/profile', '/orders'];

const shouldFetchDynamicSettings = (pathname) =>
    DYNAMIC_SETTINGS_ROUTES.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`),
    );

export const useSiteConfigContext = () => {
    const context = useContext(SiteConfigContext);
    return context;
};

export const SiteConfigProvider = ({ children }) => {
    const pathname = usePathname();
    // Build static config object (Initial state)
    const staticConfig = useMemo(() => ({
        // Site Identity
        siteName: APP_CONFIG.site.name,
        domain: APP_CONFIG.site.domain,

        // Contact Information
        contact: APP_CONFIG.site.contact,
        social: APP_CONFIG.site.social,
        hours: APP_CONFIG.site.hours,

        // Computed Values
        copyrightText: `(c) ${new Date().getFullYear()} ${APP_CONFIG.site.name}. All rights reserved. Made with love in India`,

        // Homepage Banner
        homepageBannerMode: 'default',
        homepageBanner1: null,
        homepageBanner2: null,

        // Tax Configuration (static fallback)
        tax: APP_CONFIG.site?.tax || {},

        // Feature Flags
        features: APP_CONFIG.features,
    }), []);

    const [config, setConfig] = useState(staticConfig);
    const [loading, setLoading] = useState(
        Boolean(APP_CONFIG.site.useDynamicSettings && shouldFetchDynamicSettings(pathname)),
    );

    useEffect(() => {
        // Skip fetching on public browsing routes so the homepage stays fast.
        if (!APP_CONFIG.site.useDynamicSettings || !shouldFetchDynamicSettings(pathname)) {
            setConfig(staticConfig);
            setLoading(false);
            return;
        }

        const fetchSettings = async () => {
            try {
                // Parallel fetch for speed
                const [settingsResponse, mergedTaxConfig] = await Promise.all([
                    fetch(`${APP_CONFIG.api.baseUrl}/settings/public`, {
                        headers: { 'Content-Type': 'application/json' },
                    }).then(res => res.ok ? res.json() : null),
                    getMergedTaxConfig()
                ]);

                const data = settingsResponse?.data || settingsResponse || {};
                const homepageBanner1 = getHomepageBanner1OverrideFromSettings(data);
                const homepageBanner2 = getHomepageBanner2OverrideFromSettings(data);

                setConfig(prev => ({
                    ...prev,
                    // Merge dynamic settings if available
                    siteName: data.site_title || data.siteName || prev.siteName,
                    contact: {
                        ...prev.contact,
                        email: data.contact_email || prev.contact?.email,
                        phone: data.contact_phone || prev.contact?.phone,
                        address: [
                            data.store_address,
                            data.store_city,
                            data.store_zip,
                            data.store_country,
                        ].filter(Boolean).join(', ') || prev.contact?.address,
                    },
                    
                    // Homepage Banner Config
                    ...(homepageBanner1
                        ? {
                            homepageBannerMode: homepageBanner1.mode,
                            homepageBanner1: homepageBanner1.banner,
                          }
                        : {}),
                    ...(homepageBanner2
                        ? {
                            homepageBanner2: homepageBanner2.banner,
                          }
                        : {}),

                    // Tax Config (Critical)
                    tax: mergedTaxConfig,

                    // Recompute copyright with potentially new site name
                    copyrightText: `(c) ${new Date().getFullYear()} ${data.site_title || data.siteName || prev.siteName}. All rights reserved. Made with love in India`,
                }));

            } catch (error) {
                logger.warn('[SiteConfigProvider] Failed to fetch dynamic settings, using static config:', error);
                // State remains as staticConfig (safe fallback)
            } finally {
                setLoading(false);
            }
        };

        fetchSettings();
    }, [pathname, staticConfig]);

    const value = useMemo(() => ({ ...config, loading }), [config, loading]);

    return (
        <SiteConfigContext.Provider value={value}>
            {children}
        </SiteConfigContext.Provider>
    );
};
